/**
 * SystemHealthCard — Iter-222
 *
 * Admin-only health snapshot for production email + messaging channels.
 * Tracks the surfaces rebuilt in Iter-221:
 *
 *   • Booking-link emails sent (24h)
 *   • Magic-link emails sent (24h)
 *   • Invoice reminder emails sent (24h)
 *   • Email failures (24h)
 *   • Channels auto-created via self-heal (24h)
 *   • Last successful email send timestamp
 *
 * Lets admins verify production wiring at-a-glance without role-switching
 * to a talent/client account. Mount on /admin/home (or /admin/dashboard).
 *
 * Visibility: admin-only — backend endpoint rejects non-admins.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Activity, Mail, KeyRound, FileText, AlertTriangle, MessageSquare, CheckCircle2, Loader2, CreditCard, ShieldCheck, ShieldAlert, ShieldQuestion, Timer, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../utils/api';

const verdictStyle = {
  healthy:  { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  degraded: { color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200',   dot: 'bg-amber-500' },
  silent:   { color: 'text-rose-700',    bg: 'bg-rose-50',    border: 'border-rose-200',    dot: 'bg-rose-500' },
};

const formatRelative = (iso) => {
  if (!iso) return 'never';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleString();
};

const Metric = ({ icon: Icon, label, value, testid, tone = 'default', suffix = '' }) => (
  <div className="flex items-center gap-3 py-2" data-testid={testid}>
    <div className={`rounded-md p-1.5 ${tone === 'alert' ? 'bg-rose-100 text-rose-600' : tone === 'success' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-600'}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-slate-500">{label}</p>
    </div>
    <p className={`text-base font-semibold tabular-nums ${tone === 'alert' && Number(value) > 0 ? 'text-rose-600' : 'text-slate-900'}`}>
      {value === null || value === undefined ? '—' : `${value}${suffix}`}
    </p>
  </div>
);

const SystemHealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Iter-223b — Collapse by default so the tile doesn't eat dashboard
  // real estate. Persist preference per-admin so they don't have to
  // re-collapse every page load.
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem('ievntur_system_health_expanded') === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('ievntur_system_health_expanded', expanded ? '1' : '0'); }
    catch { /* noop */ }
  }, [expanded]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/admin/system/email-channels-health');
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Unable to load health snapshot');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="system-health-card-loading">
        <CardContent className="p-4 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading system health…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-rose-200 bg-rose-50" data-testid="system-health-card-error">
        <CardContent className="p-4 flex items-center gap-2 text-sm text-rose-700">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </CardContent>
      </Card>
    );
  }

  const v = verdictStyle[data?.verdict] || verdictStyle.healthy;
  const c = data?.counters || {};

  return (
    <Card className={`border ${v.border} ${v.bg}`} data-testid="system-health-card">
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full text-left"
        data-testid="system-health-toggle"
        aria-expanded={expanded}
      >
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-sm font-semibold">
            <span className={`flex items-center gap-2 ${v.color}`}>
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <Activity className="w-4 h-4" />
              System Health · last 24h
            </span>
            <span className="flex items-center gap-1.5 text-xs">
              <span className={`w-2 h-2 rounded-full ${v.dot}`} />
              <span className={v.color} data-testid="system-health-verdict">{(data?.verdict || '').toUpperCase()}</span>
            </span>
          </CardTitle>
          <p className="text-xs text-slate-600 mt-1" data-testid="system-health-verdict-reason">
            {data?.verdict_reason}
          </p>
        </CardHeader>
      </button>
      {expanded && (
      <CardContent className="pt-0">
        {/* Communications section (Iter-222 base) */}
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-1 mb-1">Communications</p>
        <div className="divide-y divide-slate-200/60">
          <Metric
            icon={Mail}
            label="Booking-link emails sent"
            value={c.booking_request_emails_sent ?? 0}
            testid="metric-booking-request-emails"
          />
          <Metric
            icon={KeyRound}
            label="Magic-link emails sent"
            value={c.magic_link_emails_sent ?? 0}
            testid="metric-magic-link-emails"
          />
          <Metric
            icon={FileText}
            label="Invoice reminder emails sent"
            value={c.invoice_reminder_emails_sent ?? 0}
            testid="metric-invoice-reminder-emails"
          />
          <Metric
            icon={AlertTriangle}
            label="Email failures"
            value={c.email_failures ?? 0}
            testid="metric-email-failures"
            tone="alert"
          />
          <Metric
            icon={MessageSquare}
            label="Channels auto-created via self-heal"
            value={c.channels_auto_created ?? 0}
            testid="metric-channels-auto-created"
          />
        </div>

        {/* Iter-223 — Payments section */}
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-4 mb-1">Payments (Stripe)</p>
        <div className="divide-y divide-slate-200/60">
          <Metric
            icon={CreditCard}
            label="Stripe success rate"
            value={c.stripe_success_rate_pct ?? null}
            suffix="%"
            testid="metric-stripe-success-rate"
            tone={c.stripe_success_rate_pct !== null && c.stripe_success_rate_pct >= 95 ? 'success' : 'default'}
          />
          <Metric
            icon={CheckCircle2}
            label="Payments succeeded"
            value={c.stripe_payments_succeeded ?? 0}
            testid="metric-stripe-payments-succeeded"
          />
          <Metric
            icon={AlertTriangle}
            label="Payments failed"
            value={c.stripe_payments_failed ?? 0}
            testid="metric-stripe-payments-failed"
            tone="alert"
          />
        </div>

        {/* Iter-223 — Compliance section */}
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-4 mb-1">Compliance (IDV)</p>
        <div className="divide-y divide-slate-200/60">
          <Metric
            icon={ShieldQuestion}
            label="Pending approvals"
            value={c.idv_pending_approvals ?? 0}
            testid="metric-idv-pending-approvals"
          />
          <Metric
            icon={ShieldAlert}
            label="Expired IDs"
            value={c.idv_expired_ids ?? 0}
            testid="metric-idv-expired"
            tone="alert"
          />
          <Metric
            icon={ShieldCheck}
            label="Expiring within 30 days"
            value={c.idv_expiring_within_30 ?? 0}
            testid="metric-idv-expiring-30"
          />
          <Metric
            icon={Timer}
            label="Re-upload turnaround (median)"
            value={c.idv_reupload_median_hours ?? null}
            suffix={c.idv_reupload_median_hours !== null ? 'h' : ''}
            testid="metric-idv-reupload-median"
          />
        </div>

        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500" data-testid="system-health-last-send">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
          Last successful email send: <span className="font-medium text-slate-700">{formatRelative(data?.last_successful_email_at)}</span>
        </div>
      </CardContent>
      )}
    </Card>
  );
};

export default SystemHealthCard;
